import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { assign, chain, countBy, forEach, isEmpty, isNil, map, sumBy, toUpper, trim } from 'lodash';
import { first } from 'rxjs/operators';

import { meterBasedSensors } from '@app/shared/constants';
import {
  GasSensorClassification,
  SensorClassification,
  SensorDataChartTypes,
  SensorTypes,
  SpeedSensorClassification,
  TankDimensionMeasurement,
} from '@app/shared/enums';
import { ChartAnnotationModel } from '@app/shared/models/chart-annotation-model';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { StringUtility } from '@app/shared/string-utility';
import { getDecimals } from '@app/shared/utility';
import { QualityIndex } from '../models/quaility-index';
import { Sensor } from '../models/sensor';
import { SensorSettings } from '../sensor-settings';

@Injectable({
  providedIn: 'root',
})
export class SensorDataHelperService {
  constructor(private translate: TranslateService, private localNumberPipe: LocalNumberPipe, private sensorSettings: SensorSettings) {}

  public chartLabels: any;
  private sensorListTableWL: any;
  private sensorCurrentValueTranslation: string[] = [];

  public translateText(): void {
    this.translate
      .get('module.sensorData.charts')
      .pipe(first())
      .subscribe((res: any) => {
        this.chartLabels = res;
      });
    this.translate
      .get('module.sensorData.sensorListTable')
      .pipe(first())
      .subscribe((res: any) => {
        this.sensorListTableWL = res;
      });
    this.translate
      .get('module.sensorData.sensorCurrentValue')
      .pipe(first())
      .subscribe((res: any) => {
        this.sensorCurrentValueTranslation = [res.day, res.week, res.month];
      });
  }

  public getSensorSettingsInterfaces(sensorType: string) {
    const settings = this.getSensorSettingsByType(sensorType);
    return isNil(settings) ? '' : settings.interfaces || '';
  }

  getSensorSettingsDetails(sensorType: string, classification: string = SensorClassification.NONE) {
    const sensor = this.getSensorSettingsByType(sensorType);
    return (sensor?.details as any[]).find((x) => x.classification === classification);
  }

  getSensorSettingsDetailsProperties(sensorType: string, classification: string = SensorClassification.NONE) {
    const sensor = this.getSensorSettingsByType(sensorType);
    const detail = (sensor?.details as any[]).find((x) => x.classification === classification);
    return isNil(detail.properties) ? sensor?.properties : detail.properties;
  }

  getSensorSettingsProperties(sensorType: string) {
    const settings = this.getSensorSettingsByType(sensorType);
    return isNil(settings) ? '' : settings.properties || '';
  }

  private getSensorSettingsByType(sensorType: string) {
    return this.sensorSettings.settings.find((x) => x.type === sensorType?.toUpperCase());
  }

  getDataSetLabels(sensorType: string, classification: string = SensorClassification.NONE) {
    return this.getSensorSettingsDetails(sensorType, classification).datasetLabels;
  }

  getYAxisLabels(sensorType: string, classification: string = SensorClassification.NONE) {
    return this.getSensorSettingsDetails(sensorType, classification).yAxisLabels;
  }

  public getDatasets(sensorType: string, consumptionData: any[], classification: string): any[] {
    const datasets: any[] = [];
    const datasetLabels = this.getDataSetLabels(sensorType, classification);
    const yAxisLabels = this.getYAxisLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);

    let index = 0;
    const chartColors = this.chartLabels[this.getSensorSettingsDetails(sensorType, classification).colors];

    if (sensorType === SensorTypes.SPEED && classification === SpeedSensorClassification.DIRECTION) {
      const inboundProperties = properties.filter((i) => i.toLowerCase().indexOf('inbound') > -1);
      const outboundProperties = properties.filter((i) => i.toLowerCase().indexOf('outbound') > -1);

      inboundProperties.forEach((prop) => {
        const datasetLabel = datasetLabels[prop].length === 0 ? '' : `${datasetLabels[prop]}`;

        const color = chartColors[index];

        const inboundData = map(consumptionData, prop);
        const datasetInbound = {
          backgroundColor: color,
          data: inboundData,
          label: datasetLabel,
          fill: false,
          pointBackgroundColor: color,
          borderColor: color,
          pointBorderWidth: 5,
          lineTension: 0,
          measurement: this.getMeasurement(sensorType, index, ''),
          stack: 'stack',
        };

        if (index < yAxisLabels.length) {
          const yAxisID = index === 0 ? 'y' : `y${index}`;
          assign(datasetInbound, { yAxisID });
        }

        datasets.push(datasetInbound);

        index++;
      });

      index = 0;
      outboundProperties.forEach((prop) => {
        const datasetLabel = datasetLabels[prop].length === 0 ? '' : `${datasetLabels[prop]}`;

        const color = chartColors[index];

        const outboundData = map(consumptionData, prop).map((o) => (o > 0 ? -o : 0));

        const datasetOutbound = {
          backgroundColor: color,
          data: outboundData,
          label: datasetLabel,
          fill: false,
          pointBackgroundColor: color,
          borderColor: color,
          pointBorderWidth: 5,
          lineTension: 0,
          measurement: this.getMeasurement(sensorType, index, null),
          stack: 'stack',
        };

        if (index < yAxisLabels.length) {
          const yAxisID = index === 0 ? 'y' : `y${index}`;
          assign(datasetOutbound, { yAxisID });
        }

        datasets.push(datasetOutbound);
        index++;
      });
    } else {
      const spdLimitConsumption = classification === SpeedSensorClassification.SPEED_LIMIT ? this.flattenConsumptionData(consumptionData) : consumptionData;

      properties.forEach((prop) => {
        const summation =
          sensorType === SensorTypes.SPEED &&
          (classification === SpeedSensorClassification.CAR_SIZE || classification === SpeedSensorClassification.SPEED_LIMIT)
            ? sumBy(spdLimitConsumption, prop)
            : sumBy(consumptionData, prop) / countBy(consumptionData, (c) => !isNil(c[prop])).true;

        const decimal = getDecimals(summation) > 0 ? 1 : 0;
        const sumEval = Number.isNaN(summation) ? 0 : summation;

        let datasetLabel = datasetLabels[prop].length === 0 ? '' : `${datasetLabels[prop]} : ${this.localNumberPipe.transform(sumEval, decimal, decimal)}`;

        if (meterBasedSensors.includes(sensorType)) {
          datasetLabel = datasetLabel + ' ' + this.getMeasurement(sensorType, null, null);
        }

        const color = chartColors[index];
        const dataset = {
          backgroundColor: color,
          data: map(consumptionData, prop),
          label: datasetLabel,
          fill: false,
          pointBackgroundColor: color,
          borderColor: color,
          pointBorderWidth: 5,
          lineTension: 0,
          measurement: this.getMeasurement(sensorType, index, null),
          stack: 'stack',
        };

        if (index < yAxisLabels.length) {
          const yAxisID = index === 0 ? 'y' : `y${index}`;
          assign(dataset, { yAxisID });
        }
        datasets.push(dataset);
        index++;
      });
    }
    return datasets;
  }

  getSensorReadingGeneral(sensorType: string, sensorValue: any): any {
    let val = '';
    switch (sensorType) {
      case SensorTypes.DOOR:
        val = sensorValue === 'open' ? 'positive' : 'negative';
        break;
      case SensorTypes.PUSH:
        val = sensorValue === 1 ? 'positive' : 'negative';
        break;
      case SensorTypes.SMOKE:
        val = sensorValue === 1 ? 'positive' : 'negative';
        break;
      default:
        val = 'negative';
        break;
    }
    return val;
  }

  getMeasurement(sensorType: string, index: number | null, classification: string | null) {
    let measurement = '';
    const co2Index = index === 1 ? this.sensorListTableWL.common.units.temperature : this.sensorListTableWL.common.units.co2;
    const coIndex = index === 1 ? this.sensorListTableWL.common.units.temperature : this.sensorListTableWL.common.units.co;
    const particulateIndex = index === 1 ? this.sensorListTableWL.common.units.temperature : this.sensorListTableWL.common.units.particulate;

    switch (toUpper(sensorType)) {
      case SensorTypes.TEMPERATURE:
        measurement = this.sensorListTableWL.common.units.temperature;
        break;
      case SensorTypes.CARBONDIOXIDE:
        measurement = index === 0 ? this.sensorListTableWL.common.units.percentage : co2Index;
        break;
      case SensorTypes.CARBONMONOXIDE:
        measurement = index === 0 ? this.sensorListTableWL.common.units.percentage : coIndex;
        break;
      case SensorTypes.PARTICULATE:
        measurement = index === 0 ? this.sensorListTableWL.common.units.percentage : particulateIndex;
        break;
      case SensorTypes.AIRQUALITY:
        measurement = index === 0 ? this.sensorListTableWL.common.units.percentage : this.sensorListTableWL.common.units.temperature;
        break;
      case SensorTypes.FLUID:
        measurement = this.sensorListTableWL.common.units.liters;
        break;
      case SensorTypes.WATER:
      case SensorTypes.GAS:
        measurement = StringUtility.areStringsIdentical(classification as string, GasSensorClassification.ESTIMATED)
          ? this.sensorListTableWL.common.units.kwh
          : this.sensorListTableWL.common.units.cubicMeter;
        break;
      case SensorTypes.HEAT:
      case SensorTypes.ELECTRICITY:
        measurement = this.sensorListTableWL.common.units.kwh;
        break;
      case SensorTypes.DOGTOILET:
        measurement = this.sensorListTableWL.common.units.percentage;
        break;
      case SensorTypes.SOILMOISTURE:
        measurement = this.sensorListTableWL.common.units.permittivity;
        break;
      case SensorTypes.MOTION:
      case SensorTypes.SPEED:
      case SensorTypes.DOOR:
      default:
        break;
    }
    return measurement;
  }

  getSensorCurrentValue = (sensor: Sensor, chartType: string) => {
    switch (chartType) {
      case SensorDataChartTypes.daily:
        return `${sensor.motionDayCount} ${this.sensorCurrentValueTranslation[0]}`;
      case SensorDataChartTypes.weekly:
        return `${sensor.motionWeekCount} ${this.sensorCurrentValueTranslation[1]}`;
      case SensorDataChartTypes.monthly:
        return `${sensor.motionMonthCount} ${this.sensorCurrentValueTranslation[2]}`;
    }
  };

  getSensorTankDimensionGqlProperty(measurement: TankDimensionMeasurement) {
    switch (measurement) {
      case TankDimensionMeasurement.LENGTH:
        return 'tankLength';
      case TankDimensionMeasurement.WIDTH:
        return 'tankWidth';
      case TankDimensionMeasurement.HEIGHT:
        return 'tankHeight';
      case TankDimensionMeasurement.DIAMETER_A:
      case TankDimensionMeasurement.DIAMETER_D:
        return 'tankDiameter';
    }
  }

  isSensorChartStackType = (sensorType: string, classification: string = '') => {
    const sensorClassSetting = this.getSensorSettingsDetails(sensorType, classification);
    return isNil(sensorClassSetting) ? false : sensorClassSetting.stacked;
  };

  getAddress = (loc: any) => (isNil(loc) ? '' : loc.street.concat(' ', loc.houseNumber || '', '\r\n', loc.ZIP || '', ' ', loc.district || ''));

  checkAddress(metaData: any) {
    const gps = '';
    if (isNil(metaData.address) || isEmpty(trim(metaData.address.replace(/(\r\n|\n|\r)/gm, '')))) {
      return gps.concat(
        'Breite: ',
        this.localNumberPipe.transform(metaData.latitude, 6).replace(',', '.'),
        '\r\n',
        'Länge: ',
        this.localNumberPipe.transform(metaData.longitude, 6).replace(',', '.')
      );
    } else {
      return this.removeTrailingSpaces(metaData.address);
    }
  }

  removeTrailingSpaces(string: string) {
    let result = '';
    const splitString = string.split(' ');

    forEach(splitString, (str) => {
      const ss = str.trim().replace(/(\r\n|\n|\r)/gm, ' ');
      if (!isNil(ss) && ss !== '') {
        result = result + ss + ' ';
      }
    });

    return result.trim();
  }

  getChartAnnotations(sensorTypeCode: string, qualityIndices: QualityIndex[]): ChartAnnotationModel[] {
    let annotations: ChartAnnotationModel[] = [];
    if (isNil(qualityIndices) || isEmpty(qualityIndices)) {
      return annotations;
    }
    switch (sensorTypeCode) {
      case SensorTypes.SOILMOISTURE:
        annotations = [
          {
            borderColor: qualityIndices[0].color,
            value: qualityIndices[0].value,
            labelEnabled: false,
            labelContent: '',
          },
          {
            borderColor: qualityIndices[2].color,
            value: qualityIndices[2].value,
            labelEnabled: false,
            labelContent: '',
          },
        ];
        break;
      case SensorTypes.CARBONDIOXIDE:
        const red = '#E91313';
        const yellow = '#F6DD00';
        const green = '#62CC26';

        const redLowerValue = chain(qualityIndices)
          .filter((x) => x.color === red)
          .orderBy((x) => x.order)
          .first()
          .value();

        const yellowUpperValue = chain(qualityIndices)
          .filter((x) => x.color === yellow)
          .orderBy((x) => x.order)
          .last()
          .value();

        const greenUpperValue = chain(qualityIndices)
          .filter((x) => x.color === green)
          .orderBy((x) => x.order)
          .last()
          .value();

        if (redLowerValue != null && yellowUpperValue != null) {
          annotations.push({
            borderColor: redLowerValue.color,
            value: yellowUpperValue.value + 1,
            labelEnabled: false,
            labelContent: '',
          });
        }

        if (greenUpperValue != null) {
          annotations.push({
            borderColor: greenUpperValue.color,
            value: greenUpperValue.value,
            labelEnabled: false,
            labelContent: '',
          });
        }
        break;
      default:
        break;
    }
    return annotations;
  }

  flattenConsumptionData(consumptionData: any[]) {
    consumptionData.forEach((data) => {
      const carsZoneData = `cars_zone_${this.sensorSettings.getCurrentSpeedLimit()}_count`
        ? JSON.parse(data[`cars_zone_${this.sensorSettings.getCurrentSpeedLimit()}_count`])
        : null;

      data['carsCountRecommendedSpeed'] = 0;
      data['carsCountTolerableSpeed'] = 0;
      data['carsCountOverSpeed'] = 0;

      if (!isNil(carsZoneData)) {
        data['carsCountRecommendedSpeed'] = carsZoneData.carsCountRecommendedSpeed;
        data['carsCountTolerableSpeed'] = carsZoneData.carsCountTolerableSpeed;
        data['carsCountOverSpeed'] = carsZoneData.carsCountOverSpeed;
      }
    });

    return consumptionData;
  }
}
